<template>
    <div class="table-container">
        <DataTable :value="logs"
            :filters.sync="filters"
            :loading="loading"
            responsiveLayout="scroll"
            filterDisplay="row"
            class="p-datatable-sm"
            scrollable
            scrollHeight="flex"
            :paginator="true"
            :rows="perPage"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            :expandedRows.sync="expandedRows"
            @row-expand="onRowExpand"
            @row-collapse="onRowCollapse"
            :lazy="true"
            :totalRecords="totalRecords"
            @filter="fetchLogs"
            @sort="fetchLogs"
            @page="fetchLogs">

            <template #empty>
                Nenhum log de service order.
            </template>

            <Column :expander="true" />

            <Column field="origin" header="Origem">
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="reference_code" header="Código Referência">
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="success" header="Sucesso">
                <template #body="slotProps">
                    <span :class="slotProps.data.success ? 'status-success' : 'status-error'">
                        {{ slotProps.data.success ? 'Sim' : 'Não' }}
                    </span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown
                    v-model="filterModel.value"
                    :options="successOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Todos"
                    :showClear="true"
                    class="p-column-filter"
                    @change="filterCallback()"
                    appendTo="body"
                    />
                </template>
            </Column>

            <Column field="error_message" header="Mensagem">
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="attempted_at" header="Data Tentativa">
                <template #body="slotProps">
                    {{ formatDateTime(slotProps.data.attempted_at) }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <v-date-picker
                        mode="date"
                        v-model="filterModel.value"
                        @input="onDateFilterChange($event, filterModel, filterCallback)"
                        :masks="{
                            input: 'DD-MM-YYYY',
                            data: 'DD-MM-YYYY'
                        }"
                    >
                        <template v-slot="{ inputValue, inputEvents }">
                            <input
                                class="p-column-filter p-inputtext p-component"
                                :value="inputValue"
                                v-on="inputEvents"
                            />
                        </template>
                    </v-date-picker>
                </template>
            </Column>

            <template #expansion="slotProps">
                <div class="p-4 expanded-row-content" v-if="slotProps.data.data">
                    <div class="p-col-12 p-md-12 info-card">
                        <pre class="json-content">{{ formatJson(slotProps.data.data) }}</pre>
                    </div>
                </div>

                <div v-else class="p-text-center p-p-4">
                    <i class="pi pi-exclamation-circle" style="font-size: 2rem; color: #ff9800;"></i>
                    <p class="p-mt-2">Sem dados adicionais.</p>
                </div>
            </template>
        </DataTable>
    </div>
</template>

<script>
import logsService from '@/services/logs.service';
import { FilterMatchMode } from "primevue/api";

export default {
    name: 'LogServiceOrderAttemptsDataTable',
    data() {
        return {
            loading: false,
            logs: [],
            expandedRows: [],
            totalRecords: 0,
            currentPage: 1,
            perPage: 50,
            filters: {
                origin: { value: null, matchMode: FilterMatchMode.CONTAINS },
                reference_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
                success: { value: null, matchMode: FilterMatchMode.EQUALS },
                error_message: { value: null, matchMode: FilterMatchMode.CONTAINS },
                attempted_at: { value: null, matchMode: FilterMatchMode.CONTAINS },
            },
            successOptions: [
                { label: 'Sim', value: 1 },
                { label: 'Não', value: 0 },
            ],
        };
    },
    mounted() {
        this.fetchLogs();
    },
    activated() {
        this.fetchLogs();
    },
    methods: {
        onDateFilterChange(date, filterModel, filterCallback) {
            if (date) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();

                filterModel.value = `${year}-${month}-${day}`;
            } else {
                filterModel.value = null;
            }

            filterCallback();
        },
        onRowExpand(event) {
            this.expandedRows = [event.data];
        },
        onRowCollapse(event) {
            this.expandedRows = this.expandedRows.filter(row => row.id !== event.data.id);
        },
        buildQueryParams(page = 1) {
            const params = new URLSearchParams();
            params.append('page', page);

            for (const [key, filter] of Object.entries(this.filters)) {
                if (filter.value !== null && filter.value !== '') {
                    params.append(`filters[${key}]`, filter.value);
                }
            }

            return params;
        },
        async fetchLogs(event = {}) {
            try {
                this.loading = true;

                const page = event.page ? event.page + 1 : 1;
                const params = this.buildQueryParams(page);
                params.append('per_page', event.rows || this.perPage);

                const response = await logsService.getServiceOrderAttempts(params);

                this.logs = response.data;
                this.totalRecords = response.total;
                this.currentPage = response.current_page;
            } catch (error) {
                this.$toast.add({severity:'error', summary: 'Erro', detail:'Não foi possível carregar os logs de service orders.', life: 3000});
            } finally {
                this.loading = false;
            }
        },
        formatDateTime(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-PT') + ' ' + date.toLocaleTimeString('pt-PT');
        },
        formatJson(data) {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                return JSON.stringify(parsed, null, 2);
            } catch {
                return data;
            }
        }
    }
}
</script>

<style scoped>
.table-container {
    margin-top: 1rem;
    padding: 1rem;
    background: white;
    border-radius: 4px;
    border: 1px solid #dee2e6;
}

.expanded-row-content {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    margin: 0.5rem 0;
    padding: 10px;
}

.json-content {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.85rem;
    margin: 0;
}

.status-success {
    color: #28a745;
    font-weight: bold;
}

.status-error {
    color: #dc3545;
    font-weight: bold;
}

.p-datatable-sm .p-datatable-tbody > tr > td {
    padding: 0.5rem;
}
</style>
